import React, { useState, useEffect } from 'react';
import { getTelegramNotificationsStatus, setTelegramNotificationsEnabled } from '../services/apiService';
import '../styles/MenuBar.css';
import logo from '../resources/BSG_Traders.png';

const INTERVAL_OPTIONS = [
  { value: 5000,   label: '5s'  },
  { value: 10000,  label: '10s' },
  { value: 15000,  label: '15s' },
  { value: 20000,  label: '20s' },
  { value: 25000,  label: '25s' },
  { value: 30000,  label: '30s' },
  { value: 35000,  label: '35s' },
  { value: 40000,  label: '40s' },
  { value: 45000,  label: '45s' },
  { value: 50000,  label: '50s' },
  { value: 55000,  label: '55s' },
  { value: 60000,  label: '1 min'  },
  { value: 120000, label: '2 min'  },
  { value: 180000, label: '3 min'  },
  { value: 300000, label: '5 min'  },
  { value: 600000, label: '10 min' },
  { value: 900000, label: '15 min' },
];

const MenuBar = ({ config, isRunning, onStart, onStop, onExport, onLogout, autoStop, onSetAutoStop, liveExport, onSetLiveExport, notificationsEnabled = true, onNotificationsChange, refreshInterval, onIntervalChange, availableExpiries = [], activeExpiry, onExpiryChange, availableStrikes = [], activeStrike, onStrikeChange, liveLotSize, onLotSizeChange, currentUser, onManageUsers, onViewTrades, onManageRiskSettings, onViewPerformance, viewerTelegramId = null, liveTradeLock = false }) => {
  const [lotInput, setLotInput] = useState(liveLotSize != null ? String(liveLotSize) : '');
  const [showMenu, setShowMenu] = useState(window.innerWidth > 1024);
  const [debugLogging, setDebugLogging] = useState(false);

  // Sync checkbox with real backend state on mount
  useEffect(() => {
    getTelegramNotificationsStatus()
      .then(data => onNotificationsChange?.(data.enabled))
      .catch(() => {}); // backend may not be ready yet — silently ignore
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleNotificationsToggle = async (checked) => {
    onNotificationsChange?.(checked);
    try {
      await setTelegramNotificationsEnabled(checked);
    } catch (err) {
      // Revert on failure
      onNotificationsChange?.(!checked);
      console.error('Failed to toggle Telegram notifications:', err);
    }
  };

  useEffect(() => {
    const handleResize = () => {
      setShowMenu(window.innerWidth > 1024);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return (
    <nav className="menu-bar">
      <div className="menu-header">
        <button
          className="menu-toggle"
          onClick={() => setShowMenu(!showMenu)}
          aria-label="Toggle menu"
        >
          {showMenu ? '✕' : '☰'}
        </button>
        <div className="menu-brand">
          <img src={logo} alt="BSG Traders" className="menu-logo" />
          <h2>BSG Traders</h2>
        </div>
      </div>

      {showMenu && (
        <div className="menu-dropdown">
          <button
            className={isRunning ? 'menu-item danger' : 'menu-item success'}
            onClick={isRunning ? onStop : onStart}
          >
            {isRunning ? '⏹ Stop' : '▶ Start'}
          </button>

          <button className="menu-item" onClick={onExport}>
            💾 Export to CSV
          </button>

          <div className="menu-item menu-interval">
            <span className="menu-interval-label">Refresh</span>
            <select
              className="menu-interval-select"
              value={refreshInterval}
              onChange={e => onIntervalChange?.(parseInt(e.target.value))}
            >
              {INTERVAL_OPTIONS.map(opt => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
          </div>

          {availableExpiries.length > 0 && (
            <div className="menu-item menu-interval">
              <span className="menu-interval-label">Expiry</span>
              <select
                className="menu-interval-select"
                value={activeExpiry ?? ''}
                onChange={e => onExpiryChange?.(e.target.value)}
                disabled={liveTradeLock}
                title={liveTradeLock
                  ? 'Locked — a live trade is open. Changing expiry mid-trade would poll the wrong option chain for its LTP.'
                  : undefined}
              >
                {availableExpiries.map(exp => (
                  <option key={exp} value={exp}>{exp}</option>
                ))}
              </select>
            </div>
          )}

          {availableStrikes.length > 0 && (
            <div className="menu-item menu-interval">
              <span className="menu-interval-label">Strike</span>
              <select
                className="menu-interval-select"
                value={activeStrike ?? ''}
                onChange={e => onStrikeChange?.(e.target.value)}
                disabled={liveTradeLock}
                title={liveTradeLock
                  ? 'Locked — a live trade is open. Changing strike mid-trade would poll the wrong option chain for its LTP.'
                  : undefined}
              >
                <option value="">ATM (auto)</option>
                {availableStrikes.map(s => (
                  <option key={s} value={s}>{s}</option>
                ))}
              </select>
            </div>
          )}
          {liveTradeLock && (availableExpiries.length > 0 || availableStrikes.length > 0) && (
            <span className="menu-live-lock-badge" title="Expiry/Strike are locked while a trade is live to protect its LTP calculation">
              🔒 Live trade
            </span>
          )}

          <div className="menu-item menu-interval">
            <span className="menu-interval-label">Lot size</span>
            <input
              type="number"
              min="1"
              className="menu-interval-select"
              style={{ width: 56 }}
              placeholder="default"
              value={lotInput}
              onChange={e => {
                setLotInput(e.target.value);
                const n = parseInt(e.target.value);
                onLotSizeChange?.((!e.target.value || isNaN(n) || n < 1) ? null : n);
              }}
            />
          </div>

          <hr />

          <label className="menu-item checkbox">
            <input
              type="checkbox"
              checked={liveExport}
              onChange={(e) => onSetLiveExport?.(e.target.checked)}
            />
            Live Export to CSV
          </label>

          <label className="menu-item checkbox">
            <input
              type="checkbox"
              checked={notificationsEnabled}
              onChange={(e) => handleNotificationsToggle(e.target.checked)}
            />
            📬 Telegram Notifications
            {currentUser?.role === 'VIEWER' && (
              <span style={{
                marginLeft: 6,
                fontSize: 11,
                color: viewerTelegramId ? '#90caf9' : '#ff9800',
                fontWeight: 400,
              }}>
                {viewerTelegramId ? `· ${viewerTelegramId}` : '· not configured'}
              </span>
            )}
          </label>
          {currentUser?.role === 'VIEWER' && !viewerTelegramId && (
            <div style={{
              padding: '4px 12px 6px 32px',
              fontSize: 11,
              color: '#9e9e9e',
              lineHeight: 1.4,
            }}>
              Ask admin to set your Telegram chat ID in User Management
            </div>
          )}

          <label className="menu-item checkbox">
            <input
              type="checkbox"
              checked={autoStop}
              onChange={(e) => onSetAutoStop?.(e.target.checked)}
            />
            Auto Stop at 3:40pm
          </label>

          <label className="menu-item checkbox">
            <input
              type="checkbox"
              checked={debugLogging}
              onChange={(e) => setDebugLogging(e.target.checked)}
            />
            Debug Logging
          </label>

          <hr />

          <button className="menu-item" onClick={onManageRiskSettings}>
            ⚖ Risk Settings
          </button>

          <button className="menu-item" onClick={onViewPerformance}>
            📈 Performance
          </button>

          {currentUser?.role === 'ADMIN' && (
            <button className="menu-item" onClick={onManageUsers}>
              👥 Manage Users
            </button>
          )}
          {currentUser?.role === 'ADMIN' && (
            <button className="menu-item" onClick={onViewTrades}>
              📊 All Trades
            </button>
          )}

          <button className="menu-item" onClick={onLogout}>
            🚪 Logout
          </button>

          <button className="menu-item danger" onClick={() => window.close()}>
            ❌ Quit
          </button>
        </div>
      )}
    </nav>
  );
};

export default MenuBar;
